/**
 * CR060: Phase Registry - Single source of truth for pipeline phases
 * 
 * This file defines all valid pipeline phases and their metadata.
 * Used by: PackBuilder, PocRunDetail UI, poc-scan-run edge function
 */

// ============================================================================
// PHASE DEFINITIONS
// ============================================================================

export type PhaseType = 
  | 'SYSTEM'
  | 'SCHEMA'
  | 'READ'
  | 'SCAN'
  | 'READ_DETAILS'
  | 'SCAN_DETAILS'
  | 'MERGE'
  | 'EXTRACT'
  | 'VALIDATE'
  | 'NORMALIZE'
  | 'DEDUPE'
  | 'LOCATE_SNIPPET'
  | 'CROP_SNIPPET';

export interface PhaseDefinition {
  id: PhaseType;
  name: string;
  description: string;
  isConfigurationPhase: boolean;  // SYSTEM, SCHEMA - not executed as steps
  isExecutionPhase: boolean;      // Phases that create step records
  isCandidateScoped: boolean;     // Runs per-candidate vs per-merchant
  order: number;                   // Default sort order
  color: string;                   // UI color class
}

export const PHASE_REGISTRY: Record<PhaseType, PhaseDefinition> = {
  SYSTEM: {
    id: 'SYSTEM',
    name: 'System',
    description: 'System prompt context applied to all AI calls',
    isConfigurationPhase: true,
    isExecutionPhase: false,
    isCandidateScoped: false,
    order: 0,
    color: 'bg-gray-500',
  },
  SCHEMA: {
    id: 'SCHEMA',
    name: 'Schema',
    description: 'JSON schema definitions for AI output validation',
    isConfigurationPhase: true,
    isExecutionPhase: false,
    isCandidateScoped: false,
    order: 1,
    color: 'bg-blue-500',
  },
  READ: {
    id: 'READ',
    name: 'Read',
    description: 'Read and resolve content from merchant sources',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: false,
    order: 10,
    color: 'bg-cyan-500',
  },
  SCAN: {
    id: 'SCAN',
    name: 'Scan',
    description: 'Scan resolved content to find deal candidates',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: false,
    order: 20,
    color: 'bg-green-500',
  },
  READ_DETAILS: {
    id: 'READ_DETAILS',
    name: 'Read Details',
    description: 'Fetch detail/terms pages for candidates with detail_link',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 30,
    color: 'bg-teal-500',
  },
  SCAN_DETAILS: {
    id: 'SCAN_DETAILS',
    name: 'Scan Details',
    description: 'Extract relevant details from fetched detail pages',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 35,
    color: 'bg-lime-500',
  },
  MERGE: {
    id: 'MERGE',
    name: 'Merge',
    description: 'Merge detail text into candidate objects',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 40,
    color: 'bg-indigo-500',
  },
  EXTRACT: {
    id: 'EXTRACT',
    name: 'Extract',
    description: 'Extract structured deal data from candidates',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 50,
    color: 'bg-yellow-500',
  },
  VALIDATE: {
    id: 'VALIDATE',
    name: 'Validate',
    description: 'Validate extracted deals and gate false positives',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 60,
    color: 'bg-orange-500',
  },
  NORMALIZE: {
    id: 'NORMALIZE',
    name: 'Normalize',
    description: 'Normalize deal data to consistent format',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 70,
    color: 'bg-purple-500',
  },
  DEDUPE: {
    id: 'DEDUPE',
    name: 'Dedupe',
    description: 'Remove duplicate deals across sources',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: false, // Runs once on the set
    order: 80,
    color: 'bg-pink-500',
  },
  LOCATE_SNIPPET: {
    id: 'LOCATE_SNIPPET',
    name: 'Locate Snippet',
    description: 'Locate deal snippet regions within flyer page images (Pass A)',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 90,
    color: 'bg-rose-500',
  },
  CROP_SNIPPET: {
    id: 'CROP_SNIPPET',
    name: 'Crop Snippet',
    description: 'Refine deal snippet bbox via cropper model (Pass B)',
    isConfigurationPhase: false,
    isExecutionPhase: true,
    isCandidateScoped: true,
    order: 91,
    color: 'bg-rose-400',
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all phases available for Pack Builder dropdown
 */
export function getAllPhases(): PhaseType[] {
  return Object.keys(PHASE_REGISTRY) as PhaseType[];
}

/**
 * Get only execution phases (phases that create step records)
 */
export function getExecutionPhases(): PhaseType[] {
  return Object.values(PHASE_REGISTRY)
    .filter(p => p.isExecutionPhase)
    .sort((a, b) => a.order - b.order)
    .map(p => p.id);
}

/**
 * Get configuration phases (SYSTEM, SCHEMA)
 */
export function getConfigurationPhases(): PhaseType[] {
  return Object.values(PHASE_REGISTRY)
    .filter(p => p.isConfigurationPhase)
    .map(p => p.id);
}

/**
 * Get phase color for UI
 */
export function getPhaseColor(phase: string): string {
  return PHASE_REGISTRY[phase as PhaseType]?.color || 'bg-gray-500';
}

/**
 * Check if a phase is an execution phase
 */
export function isExecutionPhase(phase: string): boolean {
  return PHASE_REGISTRY[phase as PhaseType]?.isExecutionPhase || false;
}

/**
 * Check if a phase runs per-candidate
 */
export function isCandidateScopedPhase(phase: string): boolean {
  return PHASE_REGISTRY[phase as PhaseType]?.isCandidateScoped || false;
}

/**
 * Get default phase order for sorting
 */
export function getPhaseOrder(phase: string): number {
  return PHASE_REGISTRY[phase as PhaseType]?.order || 999;
}

// Default phases for fallback when no pack is configured
export const DEFAULT_PIPELINE_PHASES: PhaseType[] = [
  'READ',
  'SCAN',
  'EXTRACT',
  'VALIDATE',
];

// All phases that should appear in Pack Builder dropdown
export const PACK_BUILDER_PHASES: PhaseType[] = getAllPhases();

// Phases that create UI tabs in POC Run Detail
export const POC_RUN_DETAIL_TAB_PHASES: PhaseType[] = getExecutionPhases();
